import clsx from "clsx";
import Modal from "../Modal/Modal";
import { MdCheck, MdClose, MdOutlineList } from "react-icons/md";
import { ETransactionType } from "src/helpers/constants";
import { useTranslation } from "react-i18next";
import TextInput from "../TextInput/TextInput";
import TextAreaInput from "../TextAreaInput/TextAreaInput";
import RecurringTransactionAttachments from "./RecurringTransactionAttachments";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import CustomSelect, { ICustomSelectOption } from "../Select/CustomSelect";
import { v4 as uuidv4 } from "uuid";

import {
  calculateNextPaymentDate,
  formatFileSize,
  getTotalByTransactionType,
  showNotification,
  transformToSelectOptions,
} from "src/helpers/utils";
import useCurrentUser from "src/hooks/useCurrentUser";
import useCollapsibles from "src/hooks/useCollapsibles";
import moment from "moment";
import { ERecurringTransactionFrequency } from "./RecurringTransactionSettings";
import TransactionAttachmentsLightbox from "src/pages/NewTransaction/TransactionAttachmentsLightbox";
import useCache from "src/hooks/useCache";
import { sortBy } from "lodash";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";
import useModalsStore from "src/store/modalsStore";
import { IRecurringTransactionModalPayload } from "../Modals/FrequentTransactionModal";
import BankCard from "../BankCard/BankCard";
import useUiPreferences from "src/store/useUiPreferences";
import useAllTransactions from "src/hooks/useAllTransactions";
import useColors from "src/hooks/useColors";
import TransactionStatusMessage from "src/pages/TransactionStatusMessage/TransactionStatusMessage";
import { addTransaction, payRecurringTransaction } from "src/api/transactions";

const RecurringTransactionPayment: React.FC<
  IRecurringTransactionModalPayload
> = ({ transactionId, initialValues, mode = "register" }) => {
  const accountsUiPreferences = useUiPreferences((s) => s.accounts);
  const { getAccountColors } = useColors();

  const { setShowRecurringTransactionPaymentModal } = useModalsStore();
  const { t } = useTranslation(["modals", "common"]);
  const thumbnailsRef = useRef<any>(null);
  const { clearCache } = useCache();
  const [isSavingPayment, setIsSavingPayment] = useState<boolean>(false);
  const transactionType = (
    initialValues?.type === ETransactionType.INCOME
      ? t("common:transaction_types.income")
      : t("common:transaction_types.expense")
  ).toLowerCase();

  const [notes, setNotes] = useState<string>("");
  const [amount, setAmount] = useState<string>(
    initialValues?.total ? `${initialValues?.total}` : "",
  );
  const [date, setDate] = useState<string>(moment().format("yyyy-MM-DD"));
  const [time, setTime] = useState<string>(moment().format("HH:mm"));
  const [nextPayment, setNextPayment] = useState<string>("");
  const { updateCollapsible, collapsiblePreferences } = useCollapsibles();

  const [attachmentsGalleryIsOpen, setAttachmentsGalleryIsOpen] =
    useState<boolean>(false);
  const [clickedAttachmentIndex, setClickedAttachmentIndex] =
    useState<number>(0);
  const [showConfirmPayment, setShowConfirmPayment] = useState<boolean>(false);
  const [originAccount, setOriginAccount] = useState<string>("");
  const { allActiveTransactions } = useAllTransactions();

  const {
    allPaymentMethods,
    allEnabledPaymentMethods,
    allUserAccounts,
    enabledAccountsList,
    getAccountById,
  } = useCurrentUser();
  const paymentMethods = useMemo(() => {
    return transformToSelectOptions({
      data: allEnabledPaymentMethods,
      labelField: "name",
      valueField: "id",
    });
  }, [allEnabledPaymentMethods]);

  const onClose = () => {
    setShowRecurringTransactionPaymentModal(false, null);
  };

  const [filesToAttach, setFilesToAttach] = useState<Array<any>>([]);

  const saveIsEnabled = useMemo(() => {
    return date && time && amount && nextPayment && originAccount;
  }, [date, amount, nextPayment, time, originAccount]);

  const unifiedAttachmentsList = useMemo(() => {
    return [
      ...(filesToAttach?.map?.((theFile: any) => {
        return {
          name: theFile.name,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() ||
            t("misc:strings.unknown_file_type"),
          isSaved: false,
          thumbnailUrl: theFile?.preview,
          fileUrl: theFile?.preview,
        };
      }) || []),
    ];
  }, [filesToAttach]);

  const attachmentSlides: any[] = useMemo(() => {
    return (
      unifiedAttachmentsList?.map?.((attachment: any) => {
        return {
          type: attachment?.type === "application/pdf" ? undefined : "image",
          src: attachment?.fileUrl,
        };
      }) || []
    );
  }, [unifiedAttachmentsList]);

  useEffect(() => {
    if (date !== ERecurringTransactionFrequency.CUSTOM) {
      const newDate = calculateNextPaymentDate(
        date,
        initialValues?.frequency as ERecurringTransactionFrequency,
      );
      setNextPayment(newDate);
    }
  }, [date, initialValues?.frequency]);

  const accountsToShow = useMemo(() => {
    if (accountsUiPreferences.showInactiveAccounts) {
      return allUserAccounts.filter((account: any) => {
        return account?.currency === initialValues?.currency;
      });
    }

    return enabledAccountsList.filter((account: any) => {
      return account?.currency === initialValues?.currency;
    });
  }, [
    accountsUiPreferences.showInactiveAccounts,
    allUserAccounts,
    enabledAccountsList,
  ]);

  const accountIncome = useMemo(
    () =>
      getTotalByTransactionType({
        type: ETransactionType.INCOME,
        transactions: allActiveTransactions,
        account: originAccount,
      }),
    [allActiveTransactions, originAccount],
  );

  const accountExpenses = useMemo(
    () =>
      getTotalByTransactionType({
        type: ETransactionType.EXPENSE,
        transactions: allActiveTransactions,
        account: originAccount,
      }),
    [allActiveTransactions, originAccount],
  );

  const totalAvailableMoney = useMemo(() => {
    return accountIncome - accountExpenses;
  }, [accountIncome, accountExpenses]);

  const hasFunds = totalAvailableMoney > 0;

  const amountIsGreaterThanFunds = useMemo(() => {
    return Number(amount) > totalAvailableMoney;
  }, [amount, totalAvailableMoney]);

  const originAccountsList = useMemo(() => {
    if (!accountsToShow?.length) return [];

    return (sortBy(
      transformToSelectOptions({
        data: accountsToShow,
        labelField: "name",
        valueField: "account",
      }),
      ["label"],
    ) || []) as Array<ICustomSelectOption>;
  }, [accountsToShow]);

  const originAccountData = useMemo(() => {
    if (!originAccount) return null;

    return getAccountById(originAccount);
  }, [originAccount]);

  const showAccountSettingsMessage = useMemo(() => {
    if (originAccount && originAccountData) {
      return !hasFunds || amountIsGreaterThanFunds;
    }

    return false;
  }, [originAccount, originAccountData, hasFunds, amountIsGreaterThanFunds]);

  const availableMoney = useCallback(
    (account: any) => {
      const income = getTotalByTransactionType({
        type: ETransactionType.INCOME,
        transactions: allActiveTransactions,
        account: account,
      });
      const expenses = getTotalByTransactionType({
        type: ETransactionType.EXPENSE,
        transactions: allActiveTransactions,
        account: account,
      });

      return income - expenses;
    },
    [allActiveTransactions],
  );

  const savePayment = async () => {
    const newTransactionId = uuidv4();

    setIsSavingPayment(true);
    try {
      const data = await payRecurringTransaction({
        transactionId: transactionId || "",
        nextPayment,
        fieldsToSave: {
          invoice: newTransactionId,
          date,
          time,
          documentNumber: "",
          payTo: initialValues?.payTo,
          notes,
          taxes: 0,
          account: originAccountData.account,
          details: [],
          description: initialValues?.description,
          subTotal: initialValues?.total,
          type: initialValues?.type,
          discount: 0,
          category: initialValues?.category,
          total: amount,
          tags: [],
          paymentVoucher: "",
        },
        files: filesToAttach,
      });

      if (data?.success) {
        await clearCache();

        setShowConfirmPayment(false);
        showNotification({
          message: t("dialogs:transaction.save.notifications.success"),
          kind: "success",
        });
        onClose();
      } else {
        showNotification({
          message: t("dialogs:transaction.save.notifications.error"),
          kind: "error",
        });
      }
    } catch (e) {
      console.log(e);
      showNotification({
        message: t("dialogs:transaction.save.notifications.error"),
        kind: "error",
      });
    } finally {
      setIsSavingPayment(false);
    }
  };

  return (
    <>
      <Modal
        onEscape={() => {
          if (!isSavingPayment) onClose();
        }}
        className="md:!max-w-[600px] md:!min-w-[600px] md:!w-full"
      >
        <Modal.Header
          title={`${t("modals:register_payment.title")} ${transactionType}`}
          onClose={() => {
            if (!isSavingPayment) onClose();
          }}
        />
        <Modal.Body>
          <div
            className={clsx([
              "flex",
              "flex-col-reverse",
              "md:flex-col",
              "gap-6",
              "pb-2",
            ])}
          >
            <CollapsibleBlock
              title={t("modals:register_payment.collapsibles.details.title")}
              // open={collapsiblePreferences.recurringTransactionDetailsOpen}
              blocked
              open
              onChange={(isOpen: boolean) => {
                updateCollapsible({
                  recurringTransactionDetailsOpen: isOpen,
                });
              }}
              icon={<MdOutlineList size={ICON_SIZES.SM} />}
            >
              <div className={clsx(["flex", "flex-col", "gap-6"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-4",
                  ])}
                >
                  <TextInput
                    value={date}
                    onChange={(e: any) => setDate(e?.target?.value)}
                    label={t(
                      "modals:register_payment.collapsibles.details.fields.date.label",
                    )}
                    placeholder={t(
                      "modals:register_payment.collapsibles.details.fields.date.placeholder",
                    )}
                    type="date"
                    required
                  />
                  <TextInput
                    placeholder={t("Hora")}
                    value={time}
                    onChange={(e) => setTime(e?.target?.value)}
                    label={t("modals:transfer_funds.collapsibles.data.time")}
                    type="time"
                    required
                  />
                </div>
                <div className={clsx(["flex", "flex-col", "gap-4"])}>
                  {originAccount && (
                    <>
                      <BankCard
                        disabled={originAccountData?.settings?.disabled}
                        account={originAccountData.account}
                        currency={originAccountData.currency}
                        image={
                          originAccountData.settings?.picture?.thumbnailUrl
                        }
                        bank={
                          originAccountData.bank ||
                          t("misc:strings.unknown_bank_name")
                        }
                        name={originAccountData.name}
                        number={
                          originAccountData.number ||
                          t("pages:account.unknown_account_number")
                        }
                        customStyles={{
                          width: "100%",
                          ...getAccountColors(originAccountData?.account)
                            .string,
                        }}
                        available={availableMoney(originAccountData.account)}
                      />
                    </>
                  )}
                  <CustomSelect
                    label={t("modals:transfer_funds.collapsibles.data.origin")}
                    options={originAccountsList}
                    value={originAccount}
                    useValueAsLabel={false}
                    onChange={(e: any) => setOriginAccount(e?.value || "")}
                    required
                    placeholder={t(
                      "modals:transfer_funds.collapsibles.data.origin_placeholder",
                    )}
                  />
                  {showAccountSettingsMessage && (
                    <TransactionStatusMessage
                      amount={amount}
                      funds={totalAvailableMoney}
                      transactionType={ETransactionType.EXPENSE}
                      showButtons={false}
                    />
                  )}
                </div>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-6",
                    "md:gap-4",
                  ])}
                >
                  <TextInput
                    value={amount}
                    onChange={(e: any) => setAmount(e?.target?.value)}
                    label={t(
                      "modals:register_payment.collapsibles.details.fields.amount.label",
                    )}
                    placeholder={t(
                      "modals:register_payment.collapsibles.details.fields.amount.placeholder",
                    )}
                    type="number"
                    required
                  />
                  <TextInput
                    value={nextPayment}
                    onChange={(e: any) => setNextPayment(e?.target?.value)}
                    label={t(
                      "modals:register_payment.collapsibles.details.fields.next_transaction.label",
                    )}
                    placeholder={t(
                      "modals:register_payment.collapsibles.details.fields.next_transaction.placeholder",
                    )}
                    type="date"
                    required
                  />
                </div>
                <TextAreaInput
                  placeholder={t(
                    "modals:register_payment.collapsibles.details.fields.notes.placeholder",
                  )}
                  onChange={(e) => setNotes(e?.target?.value)}
                  value={notes}
                  rows={3}
                  label={t(
                    "modals:register_payment.collapsibles.details.fields.notes.label",
                  )}
                />
              </div>
            </CollapsibleBlock>

            <RecurringTransactionAttachments
              attachments={[]}
              onChange={({ unsaved }) => setFilesToAttach(unsaved)}
              onClick={({
                saved,
                index,
              }: {
                saved: boolean;
                index: number;
              }) => {
                setClickedAttachmentIndex(index);
                setAttachmentsGalleryIsOpen(true);
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer
          actions={[
            {
              text: t("common:buttons.save"),
              onClick: () => setShowConfirmPayment(true),
              kind: EButtonKind.SAVE,
              icon: <MdCheck size={ICON_SIZES.SM} />,
              disabled: !saveIsEnabled || isSavingPayment,
              isLoading: isSavingPayment,
            },
            {
              text: t("common:buttons.close"),
              onClick: () => {
                if (!isSavingPayment) onClose();
              },
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.SM} />,
              disabled: isSavingPayment,
            },
          ]}
        />
      </Modal>
      {attachmentsGalleryIsOpen && (
        <TransactionAttachmentsLightbox
          slides={attachmentSlides}
          index={clickedAttachmentIndex}
          setIndex={setClickedAttachmentIndex}
          thumbnailsRef={thumbnailsRef}
          unifiedAttachmentsList={unifiedAttachmentsList}
          onClose={() => setAttachmentsGalleryIsOpen(false)}
        />
      )}
      {showConfirmPayment && (
        <ConfirmDialog
          description={`¿Deseas registrar el ${transactionType}?`}
          onClose={() => {
            if (!isSavingPayment) {
              setShowConfirmPayment(false);
            }
          }}
          title={`Guardar`}
          isLoading={isSavingPayment}
          onConfirm={async () => {
            await savePayment();
          }}
        />
      )}
    </>
  );
};

export default RecurringTransactionPayment;
